<template>
    <kiosk-layout>
        <div>
            <page-callout section="kiosk-tcr-brand" class="mb-4" />
            <page-header>
                <span class="flex items-center gap-2">
                    <vx-badge color="slate" size="xs" class="font-mono">{{ props.brandId }}</vx-badge>
                    {{ details?.brand.display_name ?? 'Brand' }}
                </span>

                <template v-slot:actions>
                    <vx-button
                        @click="openEdit()"
                        size="lg"
                        color="muted-dark"
                        data-test="edit"
                        :disabled="!editAllowed"
                    >
                        Edit <font-awesome-icon widthAuto :icon="faPen" />
                    </vx-button>
                    <vx-button
                        :href="`/kiosk/ten-dlc/campaigns/new?brand_id=${props.brandId}`"
                        size="lg"
                        color="muted-dark"
                        data-test="new-campaign"
                        :disabled="!campaignsAllowed"
                    >
                        New Campaign <font-awesome-icon widthAuto :icon="faPlus" />
                    </vx-button>
                    <vx-button
                        @click="revetModal = true"
                        size="lg"
                        :color="revetAllowed ? 'primary' : 'muted-dark'"
                        data-test="verify-identity"
                        :disabled="!revetAllowed"
                    >
                        Verify Identity <font-awesome-icon widthAuto :icon="faArrowsRotate" />
                    </vx-button>
                    <vx-button
                        @click="vettingModal = true"
                        size="lg"
                        :color="vettingAllowed ? 'primary' : 'muted-dark'"
                        data-test="request-vetting"
                        :disabled="!vettingAllowed"
                    >
                        Request Vetting <font-awesome-icon widthAuto :icon="faShieldCheck" />
                    </vx-button>
                </template>
            </page-header>

            <page-loading v-if="loading"></page-loading>

            <div v-else-if="details" class="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <vx-expandable-card data-test="identity-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Identity</div>
                        <vx-alert v-if="deleted" color="slate" :closeable="false" class="mb-4" data-test="deleted">
                            This brand is deleted at TCR and cannot be changed.
                        </vx-alert>
                        <dl class="flex flex-col gap-3 text-sm">
                            <div>
                                <dt class="font-bold">Legal name</dt>
                                <dd class="text-slate-500">{{ details.brand.company_name ?? '—' }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Universal EIN</dt>
                                <dd class="text-slate-500">
                                    <span v-if="details.brand.universal_ein" class="font-mono">{{
                                        details.brand.universal_ein
                                    }}</span>
                                    <span v-else>—</span>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Identity status</dt>
                                <dd>
                                    <vx-badge :color="identityColor" size="xs" data-test="identity-status">{{
                                        identityLabel
                                    }}</vx-badge>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Entity type</dt>
                                <dd class="text-slate-500">{{ details.brand.entity_type ?? '—' }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Vertical</dt>
                                <dd class="text-slate-500">{{ details.brand.vertical ?? '—' }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Relationship</dt>
                                <dd class="text-slate-500">{{ relationshipLabel }}</dd>
                            </div>
                            <div>
                                <dt class="font-bold">Contact</dt>
                                <dd class="text-slate-500">
                                    {{ details.brand.email ?? '—' }} · {{ details.brand.phone ?? '—' }}
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Tags</dt>
                                <dd>
                                    <vx-badge
                                        v-for="tag in details.brand.tag"
                                        :key="tag"
                                        size="xs"
                                        color="slate"
                                        class="mr-1"
                                    >
                                        {{ tag }}
                                    </vx-badge>
                                    <span v-if="!details.brand.tag.length" class="text-slate-500">—</span>
                                </dd>
                            </div>
                            <div>
                                <dt class="font-bold">Reference id</dt>
                                <dd class="text-slate-500">
                                    <span v-if="details.brand.reference_id" class="font-mono">{{
                                        details.brand.reference_id
                                    }}</span>
                                    <span v-else>—</span>
                                </dd>
                            </div>
                        </dl>
                        <vx-alert
                            v-if="identityPending"
                            color="info"
                            :closeable="false"
                            class="mt-4"
                            data-test="identity-pending"
                        >
                            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <span>TCR is still checking this brand's identity.</span>
                                <vx-button @click="load()" size="sm" color="info" data-test="check-again">
                                    Check again
                                </vx-button>
                            </div>
                        </vx-alert>
                        <vx-alert
                            v-if="details.feedback?.length"
                            color="danger"
                            :closeable="false"
                            class="mt-4"
                            data-test="identity-feedback"
                        >
                            <div class="flex flex-col gap-3">
                                <div
                                    v-for="(category, index) in details.feedback"
                                    :key="index"
                                    :data-test="`feedback-${category.id}`"
                                >
                                    <div class="font-bold">{{ category.display_name ?? category.id }}</div>
                                    <div v-if="category.description">{{ category.description }}</div>
                                    <div v-if="category.fields.length">
                                        Fields: {{ category.fields.map(brandFieldLabel).join(', ') }}
                                    </div>
                                    <ul v-if="category.errors.length" class="list-disc pl-5">
                                        <li v-for="(error, errorIndex) in category.errors" :key="errorIndex">
                                            {{ error.message ?? error.code ?? '—' }}
                                        </li>
                                    </ul>
                                </div>
                                <div class="font-bold">
                                    Edit the brand to fix these, then Verify Identity; Appeal if the details are already
                                    right.
                                </div>
                            </div>
                        </vx-alert>
                        <div v-if="unverified" class="mt-4">
                            <vx-button
                                @click="openAppeal()"
                                size="sm"
                                color="muted-dark"
                                data-test="appeal"
                                :disabled="!appealAllowed"
                            >
                                Appeal <font-awesome-icon widthAuto :icon="faGavel" />
                            </vx-button>
                        </div>
                        <div v-if="details.appeals.length" class="mt-4" data-test="appeals">
                            <div class="font-bold mb-2">Appeals</div>
                            <vx-table class="xl:grid-cols-4" rowBreakpoint="xl">
                                <vx-table-row isHeader>
                                    <div>Status</div>
                                    <div>Categories</div>
                                    <div>Submitted</div>
                                    <div>Outcome</div>
                                </vx-table-row>
                                <vx-table-row
                                    v-for="(appeal, index) in details.appeals"
                                    :key="index"
                                    :data-test="`appeal-${appeal.appeal_status}`"
                                >
                                    <vx-table-row-title>
                                        {{ appeal.appeal_status ? appealStatusLabels[appeal.appeal_status] : '—' }}
                                    </vx-table-row-title>
                                    <vx-table-row-item>
                                        <template v-slot:label> Categories: </template>
                                        {{ appeal.categories.map(appealCategoryLabel).join(', ') || '—' }}
                                    </vx-table-row-item>
                                    <vx-table-row-item>
                                        <template v-slot:label> Submitted: </template>
                                        {{ appeal.create_date ? shortDateTime(appeal.create_date) : '—' }}
                                    </vx-table-row-item>
                                    <vx-table-row-item>
                                        <template v-slot:label> Outcome: </template>
                                        {{ outcomeLabel(appeal) }}
                                        <div v-if="appeal.outcome?.feedback.length" class="text-slate-500">
                                            {{ appeal.outcome.feedback.map(feedbackLabel).join(', ') }}
                                        </div>
                                    </vx-table-row-item>
                                </vx-table-row>
                            </vx-table>
                        </div>
                        <div class="mt-4">
                            <vx-button
                                @click="deleteModal = true"
                                size="sm"
                                outline
                                color="danger"
                                data-test="delete"
                                :disabled="!deleteAllowed"
                            >
                                Delete <font-awesome-icon widthAuto :icon="faTrash" />
                            </vx-button>
                        </div>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card data-test="vetting-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Vetting</div>
                        <vx-alert v-if="!details.vettings.length" color="slate" :closeable="false">
                            Not vetted. An unvetted brand gets the lowest trust tier (T-Mobile cap of 2,000 messages a
                            day); request a standard vet once the brand is verified.
                        </vx-alert>
                        <vx-table v-else class="xl:grid-cols-5" rowBreakpoint="xl">
                            <vx-table-row isHeader>
                                <div>Provider</div>
                                <div>Class</div>
                                <div>Status</div>
                                <div>Score</div>
                                <div>Vetted</div>
                            </vx-table-row>
                            <vx-table-row
                                v-for="vetting in details.vettings"
                                :key="vetting.vetting_id ?? vetting.create_date ?? ''"
                                :data-test="`vetting-${vetting.vetting_status}`"
                            >
                                <vx-table-row-title>{{ vetting.evp_id ?? '—' }}</vx-table-row-title>
                                <vx-table-row-item>
                                    <template v-slot:label> Class: </template>
                                    {{ vetting.vetting_class ?? '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Status: </template>
                                    {{ vetting.vetting_status ? vettingStatusCodeLabels[vetting.vetting_status] : '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Score: </template>
                                    <strong>{{ vetting.vetting_score ?? '—' }}</strong>
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Vetted: </template>
                                    {{ vetting.vetted_date ? shortDateTime(vetting.vetted_date) : '—' }}
                                </vx-table-row-item>
                            </vx-table-row>
                        </vx-table>
                        <vx-alert v-if="topTier" color="success" :closeable="false" class="mt-4" data-test="top-tier">
                            Score {{ bestScore }} is at least {{ TOP_TIER_SCORE }}: already the top tier, an enhanced
                            vet buys nothing.
                        </vx-alert>
                    </template>
                </vx-expandable-card>

                <kiosk-tcr-qualification-table v-if="!identityPending" :brandId="props.brandId" class="xl:col-span-2" />

                <vx-expandable-card data-test="campaigns-card">
                    <template v-slot:content>
                        <div class="text-xl font-extrabold mb-4">Campaigns in TCR</div>
                        <vx-alert v-if="!details.campaigns.length" color="slate" :closeable="false">
                            No campaigns are registered under this brand.
                        </vx-alert>
                        <vx-table v-else class="xl:grid-cols-5" rowBreakpoint="xl">
                            <vx-table-row isHeader>
                                <div>Campaign ID</div>
                                <div>Use Case</div>
                                <div>Status</div>
                                <div>DCA complete</div>
                                <div>Terms</div>
                            </vx-table-row>
                            <vx-table-row
                                v-for="campaign in details.campaigns"
                                :key="campaign.campaign_id"
                                :data-test="`campaign-${campaign.campaign_id}`"
                            >
                                <vx-table-row-title>
                                    <a
                                        :href="`/kiosk/ten-dlc/campaigns/${campaign.campaign_id}`"
                                        class="font-mono text-sm font-bold"
                                        data-test="campaign-link"
                                    >
                                        {{ campaign.campaign_id }}
                                    </a>
                                </vx-table-row-title>
                                <vx-table-row-item>
                                    <template v-slot:label> Use Case: </template>
                                    {{ campaign.usecase ?? '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Status: </template>
                                    {{ campaign.status ?? '—' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> DCA complete: </template>
                                    {{ campaign.dca_completed_at ? shortDateTime(campaign.dca_completed_at) : 'No' }}
                                </vx-table-row-item>
                                <vx-table-row-item>
                                    <template v-slot:label> Terms: </template>
                                    <span data-test="better-terms">{{
                                        termsLabel(campaign.better_terms_available ?? null)
                                    }}</span>
                                </vx-table-row-item>
                            </vx-table-row>
                        </vx-table>
                    </template>
                </vx-expandable-card>

                <kiosk-tcr-teams-list :teams="details.teams" />
            </div>

            <kiosk-tcr-brand-edit-modal
                v-if="details"
                v-model:visible="editModal"
                :brandId="props.brandId"
                :brand="details.brand"
                :enums="enums"
                :identityLocked="identityLocked"
                @updated="load()"
            />

            <kiosk-tcr-brand-vetting-modal
                v-model:visible="vettingModal"
                :brandId="props.brandId"
                @requested="load()"
            />

            <kiosk-tcr-brand-appeal-modal
                v-model:visible="appealModal"
                :brandId="props.brandId"
                :enums="enums"
                @submitted="load()"
            />

            <kiosk-tcr-brand-delete-modal
                v-model:visible="deleteModal"
                :brandId="props.brandId"
                :displayName="details?.brand.display_name ?? null"
            />

            <vx-modal v-model:visible="revetModal" size="sm" padding="standard">
                <template v-slot:header>Verify identity</template>
                <template v-slot:subheader>{{ props.brandId }}</template>

                <div class="text-sm text-slate-800">
                    <strong>Save your edits first: TCR refuses to re-check an unchanged brand.</strong>
                    <div class="mt-2">It re-checks the legal company name, EIN and entity type.</div>
                </div>

                <template v-slot:footer>
                    <div class="flex flex-col-reverse gap-4 lg:flex-row">
                        <vx-button
                            @click="revetModal = false"
                            type="button"
                            class="grow lg:w-20 lg:grow-0"
                            size="lg"
                            color="muted"
                        >
                            Cancel
                        </vx-button>
                        <vx-button
                            @click="revet()"
                            data-test="confirm-verify-identity"
                            :loading="revetting"
                            type="button"
                            class="grow"
                            size="lg"
                            color="primary"
                        >
                            Verify
                        </vx-button>
                    </div>
                </template>
            </vx-modal>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faArrowsRotate, faGavel, faPen, faPlus, faShieldCheck, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxExpandableCard,
    VxModal,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTcrBrandAppealModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandAppealModal.vue';
import KioskTcrBrandDeleteModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandDeleteModal.vue';
import KioskTcrBrandEditModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandEditModal.vue';
import KioskTcrBrandVettingModal from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandVettingModal.vue';
import KioskTcrQualificationTable from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrQualificationTable.vue';
import KioskTcrTeamsList from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrTeamsList.vue';
import {
    appealStatusLabels,
    brandFieldLabel,
    brandRelationshipLabels,
    identityStatusColors,
    identityStatusLabels,
    registrableEntityTypes,
    TOP_TIER_SCORE,
    vettingStatusCodeLabels,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { termsLabel } from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type {
    TcrBrandAppeal,
    TcrEnums,
    TcrIdentityStatus,
    TcrLiveBrandDetails,
    TcrLiveBrandFeedback,
} from '~/services/adminTenDlcRegistrationClient.types';
import { shortDateTime } from '~/utils/date';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ brandId: string }>();

const toasted = useToasted();

const details = ref<TcrLiveBrandDetails | null>(null);
const enums = ref<TcrEnums | null>(null);
const loading = ref(true);
const editModal = ref(false);
const vettingModal = ref(false);
const revetModal = ref(false);
const appealModal = ref(false);
const deleteModal = ref(false);
const revetting = ref(false);

// A brand page that is neither edited nor appealed costs no enum request.
const loadEnums = () => {
    if (null !== enums.value) {
        return;
    }

    adminTenDlcRegistrationClient
        .getEnums()
        .then((response) => {
            enums.value = response.data;
        })
        .catch((e) => {
            console.error(e);
            toasted.global.platform_error();
        });
};

const load = () => {
    loading.value = true;
    adminTenDlcRegistrationClient
        .getBrand(props.brandId)
        .then((response) => {
            details.value = response.data;

            if (response.data.appeals.length) {
                loadEnums();
            }
        })
        .catch((e) => {
            toasted.error(catchRequestError(e), { sticky: true });
        })
        .finally(() => {
            loading.value = false;
        });
};

const deleted = computed(() => 'DELETED' === details.value?.brand.status);
const identityPending = computed(() => !deleted.value && (details.value?.identity_pending ?? false));
const identityLabel = computed(() => {
    if (identityPending.value) {
        return 'Verifying';
    }

    return details.value?.brand.identity_status ? identityStatusLabels[details.value.brand.identity_status] : '—';
});
const identityColor = computed(() =>
    identityPending.value ? 'slate' : identityStatusColors[details.value?.brand.identity_status ?? 'SELF_DECLARED']
);
const relationshipLabel = computed(() =>
    details.value?.brand.brand_relationship ? brandRelationshipLabels[details.value.brand.brand_relationship] : '—'
);
const editAllowed = computed(() => {
    const entityType = details.value?.brand.entity_type ?? null;

    return !deleted.value && null !== entityType && registrableEntityTypes.includes(entityType);
});
// TCR refuses identity changes while a vet is pending or active, or a campaign is active (537).
const identityLocked = computed(
    () =>
        (details.value?.vettings ?? []).some((vetting) =>
            ['PENDING', 'UNSCORE', 'ACTIVE'].includes(vetting.vetting_status ?? '')
        ) || (details.value?.campaigns ?? []).some((campaign) => 'ACTIVE' === campaign.status)
);
const bestScore = computed(() =>
    Math.max(
        -1,
        ...(details.value?.vettings ?? [])
            .filter(
                (vetting) =>
                    'ACTIVE' === vetting.vetting_status &&
                    ['STANDARD', 'ENHANCED'].includes(vetting.vetting_class ?? '')
            )
            .map((vetting) => vetting.vetting_score ?? -1)
    )
);
const topTier = computed(() => bestScore.value >= TOP_TIER_SCORE);
const isVerified = computed(() => ['VERIFIED', 'VETTED_VERIFIED'].includes(details.value?.brand.identity_status ?? ''));
const unverified = computed(() => 'UNVERIFIED' === details.value?.brand.identity_status);
const vettingAllowed = computed(
    () =>
        isVerified.value &&
        !deleted.value &&
        !(details.value?.vettings ?? []).some((vetting) =>
            ['PENDING', 'UNSCORE'].includes(vetting.vetting_status ?? '')
        )
);
const revetAllowed = computed(() => unverified.value && !identityPending.value && !deleted.value);
const appealAllowed = computed(
    () =>
        unverified.value &&
        !identityPending.value &&
        !deleted.value &&
        !(details.value?.appeals ?? []).some((appeal) => 'PENDING' === appeal.appeal_status)
);
// TCR refuses to delete a brand with an active campaign (542); the page lists the first 50, TCR is the backstop.
const deleteAllowed = computed(
    () =>
        null !== details.value &&
        !deleted.value &&
        !details.value.campaigns.some((campaign) => 'ACTIVE' === campaign.status)
);
const campaignsAllowed = computed(() => isVerified.value && !deleted.value);

const appealCategoryLabel = (id: string): string =>
    enums.value?.brand_appeal_categories.find((category) => category.value === id)?.label ?? id;
const vetStatusLabel = (status: string): string => identityStatusLabels[status as TcrIdentityStatus] ?? status;
const outcomeLabel = (appeal: TcrBrandAppeal): string =>
    appeal.outcome?.vet_status ? vetStatusLabel(appeal.outcome.vet_status) : '—';
const feedbackLabel = (category: TcrLiveBrandFeedback): string => category.display_name ?? category.id ?? '—';

const openEdit = () => {
    loadEnums();
    editModal.value = true;
};

const openAppeal = () => {
    loadEnums();
    appealModal.value = true;
};

const revet = async () => {
    revetting.value = true;
    try {
        const response = await adminTenDlcRegistrationClient.revetBrand(props.brandId);
        toasted.success(
            ['VERIFIED', 'VETTED_VERIFIED'].includes(response.data.identity_status ?? '')
                ? 'Identity verified.'
                : 'Identity check requested.'
        );
        revetModal.value = false;
        load();
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
    } finally {
        revetting.value = false;
    }
};

load();
</script>
